/**
 * StartTrialGate — the trial wall for a user with no active/trialing
 * subscription. Coaching sessions are gated behind starting the 14-day
 * card-required trial (auto-renews into the chosen tier). Returning/lapsed
 * subscribers see "resubscribe" framing instead of a (no-longer-offered) trial.
 *
 * reason: 'planning_week_over' when the free post-onboarding planning week has
 *   ended (copy acknowledges what they've already had); null for the plain
 *   "start your trial" framing (e.g. tapped from the free-week banner).
 * onBack: when set, renders a "Back to today" link (the user still has free
 *   planning available and chose to look at the trial).
 */

import { useEffect, useState } from 'react';
import { trackAuthed } from '../lib/analytics';
import { useNudge } from '../lib/hints';
import Nudge from './Nudge';
import { Sparkles, ArrowLeft } from 'lucide-react';
import { createCheckoutSession } from '../lib/api';
import PricingTable from './PricingTable';

export default function StartTrialGate({ user, subscription, reason = null, onBack = null }) {
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [error, setError] = useState(null);
  let intent = null;
  try { intent = JSON.parse(localStorage.getItem('nav_pricing_intent') || 'null'); } catch { intent = null; }
  const intentTier = intent && ['pro', 'entrepreneur'].includes(intent.tier) ? intent.tier : null;

  useEffect(() => { trackAuthed('paywall_view', { reason: reason || 'default', returnee: !!user?.subscription_started_at, intent: intentTier }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A user who has subscribed before is not eligible for a second free trial
  // (the backend enforces this too — returnees resubscribe with no fresh trial).
  const isReturnee = !!user?.subscription_started_at;
  const weekOver = reason === 'planning_week_over';
  const wallNudge = useNudge('trial_wall', true);

  let heading = 'Start your 14-day free trial';
  let blurb = "Full access to your Navigator for 14 days. You won't be charged today. We'll email you 3 days before your trial ends, and you can cancel anytime.";
  if (isReturnee) {
    heading = 'Welcome back';
    blurb = 'Resubscribe to pick up where you left off. Your history and progress are right where you left them.';
  } else if (weekOver) {
    heading = 'Your free planning week is done';
    blurb = "You've seen how mornings work with the Navigator. The trial adds coaching sessions, evening review and full memory of what you said. No charge today, and you can cancel anytime.";
  }

  const handleSelectTier = async (tier, interval = 'monthly') => {
    setError(null);
    trackAuthed('checkout_click', { tier, interval, source: 'trial_gate' });
    setCheckoutLoading(`${tier}_${interval}`);
    try {
      const res = await createCheckoutSession(tier, interval);
      if (res.checkout_url) {
        window.location.assign(res.checkout_url);  // full redirect to Stripe Checkout
        return;
      }
      if (res.plan_changed) {
        window.location.reload();
        return;
      }
      setError(res.error || 'Could not start checkout. Please try again.');
      setCheckoutLoading(null);
    } catch (e) {
      setError('Could not start checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="h-full bg-slate-950 px-safe-4 py-8 overflow-y-auto">
      <div className="max-w-3xl mx-auto">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 mb-4 transition-colors"
          >
            <ArrowLeft size={14} /> Back to today
          </button>
        )}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600/20 border border-blue-600/40 mb-4">
            <Sparkles size={20} className="text-blue-400" />
          </div>
          <h1 className="text-2xl font-display font-semibold text-slate-100 mb-2">
            {heading}
          </h1>
          <p className="text-sm text-slate-400 max-w-md mx-auto">
            {blurb}
          </p>
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-300 bg-red-900/20 border border-red-900/40 rounded-md px-3 py-2 text-center">
            {error}
          </div>
        )}

        {intentTier && !isReturnee && (
          <p className="text-center text-xs text-indigo-300 mb-3">You picked {intentTier === 'pro' ? 'Pro' : 'Entrepreneur'} earlier. Start the trial on it below.</p>
        )}
        <PricingTable
          currentTier={subscription?.tier || 'free'}
          onSelectTier={handleSelectTier}
          isAuthenticated={true}
          checkoutLoading={checkoutLoading}
          trialEligible={!isReturnee}
          highlightTier={intentTier}
        />

        {!isReturnee && (
          <p className="text-center text-xs text-slate-500 mt-4">
            Cancel anytime before your trial ends and you won't be charged.
          </p>
        )}
        {wallNudge.show && (
          <Nudge title="What stays free" text="Your first Deep Rest and your history are yours either way. The trial adds coaching, evening review, daily Deep Rest, and full memory. No charge today." onDismiss={wallNudge.dismiss} />
        )}
      </div>
    </div>
  );
}
